import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartBar,
    faFileAlt,
    faUsers,
    faMagnifyingGlass,
    faPlus,
    faClock,
    faSpinner,
    faSort,
    faSortUp,
    faSortDown,
    faFilter,
    faTable,
    faChartPie
} from '@fortawesome/free-solid-svg-icons';
import Modal from '../Common/Modal';
import apiHelper from '../../utils/apiHelper';
import './SurveysManagement.css';
import SurveyDetailsModal from './SurveyDetailsModal';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    RadialLinearScale,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Bar, Doughnut, Pie, Radar, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    RadialLinearScale,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const SurveysManagement = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [surveyData, setSurveyData] = useState({
        responseCount: 0,
        summary: null,
        priorities: null
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [showComingSoon, setShowComingSoon] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        key: 'Created',
        direction: 'desc'
    });
    const [filters, setFilters] = useState({
        companyType: '',
        companyStructure: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        pageSizeOptions: [5, 10, 25, 50]
    });
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('table'); // 'table' or 'reports'
    const [questionnaireData, setQuestionnaireData] = useState({});

    useEffect(() => {
        fetchSurveyData();
    }, []);

    const fetchSurveyData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get initial data
            const [countResponse, summaryResponse, prioritiesResponse] = await Promise.all([
                apiHelper.get('/api/surveys/responses/count'),
                apiHelper.get('/api/surveys/responses/summary'),
                apiHelper.get('/api/surveys/responses/priorities')
            ]);

            // Fetch questionnaire data for each response
            const responses = summaryResponse.data.responses;
            const enhancedResponses = await Promise.all(
                responses.map(async (response) => {
                    try {
                        const questionnaireResponse = await apiHelper.get(
                            `/api/surveys/responses/${response.RespondentID}/questionnaire`
                        );
                        return {
                            ...response,
                            questionnaireData: questionnaireResponse.data
                        };
                    } catch (error) {
                        console.error(`Error fetching questionnaire data for ${response.RespondentID}:`, error);
                        return response;
                    }
                })
            );

            // Update summary with enhanced responses
            const enhancedSummary = {
                ...summaryResponse.data,
                responses: enhancedResponses
            };


            setSurveyData({
                responseCount: countResponse.data.count,
                summary: enhancedSummary,
                priorities: prioritiesResponse.data
            });

        } catch (err) {
            console.error('Error fetching survey data:', err);
            setError('Failed to load survey data. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleNewSurvey = () => {
        setShowComingSoon(true);
    };

    // Sort function
    const sortData = (data, sortConfig) => {
        if (!sortConfig.key) return data;

        return [...data].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    // Filter function
    const filterData = (data) => {
        return data.filter(item => {
            return (
                (!filters.companyType || item.CompanyType === filters.companyType) &&
                (!filters.companyStructure || item.CompanyStructure === filters.companyStructure)
            );
        });
    };

    // Get unique values for filter options
    const getUniqueValues = (data, key) => {
        return [...new Set(data.map(item => item[key]))].filter(Boolean);
    };

    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    // Render sort icon
    const renderSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) return <FontAwesomeIcon icon={faSort} className="sort-icon" />;
        return sortConfig.direction === 'asc' 
            ? <FontAwesomeIcon icon={faSortUp} className="sort-icon active" />
            : <FontAwesomeIcon icon={faSortDown} className="sort-icon active" />;
    };

    // Filter panel component
    const FilterPanel = ({ data }) => {
        const companyTypes = getUniqueValues(data, 'CompanyType');
        const companyStructures = getUniqueValues(data, 'CompanyStructure');

        return (
            <div className="filter-panel">
                <div className="filter-group">
                    <label>Company Type</label>
                    <select 
                        value={filters.companyType} 
                        onChange={e => setFilters(prev => ({ ...prev, companyType: e.target.value }))}>
                        <option value="">All</option>
                        {companyTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Company Structure</label>
                    <select 
                        value={filters.companyStructure} 
                        onChange={e => setFilters(prev => ({ ...prev, companyStructure: e.target.value }))}>
                        <option value="">All</option>
                        {companyStructures.map(structure => (
                            <option key={structure} value={structure}>{structure}</option>
                        ))}
                    </select>
                </div>
                <button 
                    className="btn-clear-filters" 
                    onClick={() => setFilters({ companyType: '', companyStructure: '' })}>
                    Clear Filters
                </button>
            </div>
        );
    };

    // In your table section:
    const processedData = sortData(
        filterData(surveyData.summary?.responses || []),
        sortConfig
    );

    // Pagination handlers
    const handlePageChange = (newPage) => {
        setPagination(prev => ({
            ...prev,
            currentPage: newPage
        }));
    };

    const handlePageSizeChange = (event) => {
        const newSize = Number(event.target.value);
        setPagination(prev => ({
            ...prev,
            pageSize: newSize,
            currentPage: 1 // Reset to first page when changing page size
        }));
    };

    // Get paginated data
    const getPaginatedData = (data) => {
        const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        return data.slice(startIndex, endIndex);
    };

    // Calculate total pages
    const totalPages = Math.ceil((processedData?.length || 0) / pagination.pageSize);

    // Pagination component
    const Pagination = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;
        
        let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        // Adjust start if we're near the end
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="pagination-container">
                <div className="pagination-controls">
                    <button 
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.currentPage === 1}
                        className="pagination-button"
                    >
                        First
                    </button>
                    <button 
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                        className="pagination-button"
                    >
                        Previous
                    </button>
                    
                    {startPage > 1 && <span className="pagination-ellipsis">...</span>}
                    
                    {pageNumbers.map(number => (
                        <button
                            key={number}
                            onClick={() => handlePageChange(number)}
                            className={`pagination-button ${pagination.currentPage === number ? 'active' : ''}`}
                        >
                            {number}
                        </button>
                    ))}
                    
                    {endPage < totalPages && <span className="pagination-ellipsis">...</span>}
                    
                    <button 
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === totalPages}
                        className="pagination-button"
                    >
                        Next
                    </button>
                    <button 
                        onClick={() => handlePageChange(totalPages)}
                        disabled={pagination.currentPage === totalPages}
                        className="pagination-button"
                    >
                        Last
                    </button>
                </div>
                <div className="pagination-info">
                    <select 
                        value={pagination.pageSize}
                        onChange={handlePageSizeChange}
                        className="page-size-select"
                    >
                        {pagination.pageSizeOptions.map(size => (
                            <option key={size} value={size}>
                                {size} per page
                            </option>
                        ))}
                    </select>
                    <span className="pagination-summary">
                        Showing {Math.min((pagination.currentPage - 1) * pagination.pageSize + 1, processedData.length)} 
                        - {Math.min(pagination.currentPage * pagination.pageSize, processedData.length)} 
                        of {processedData.length} entries
                    </span>
                </div>
            </div>
        );
    };

    const handleRowClick = async (response) => {
        setSelectedResponse(response);
        setDetailsLoading(true);
        
        try {
            // Fetch additional details if needed
            const detailedData = await fetchSurveyDetails(response.RespondentID);
            setSelectedResponse(detailedData);
        } catch (error) {
            console.error('Error fetching survey details:', error);
        } finally {
            setDetailsLoading(false);
        }
    };

    const fetchSurveyDetails = async (respondentId) => {
        try {            
            // Fetch additional details from each list
            const [questionnaireData, priorityTopics] = await Promise.all([
                apiHelper.get(`/api/surveys/responses/${respondentId}/questionnaire`),
                apiHelper.get(`/api/surveys/responses/${respondentId}/priorities`)
            ]);

            // Combine the data with the existing response data
            const detailedData = {
                ...processedData.find(r => r.RespondentID === respondentId),
                ...questionnaireData.data,
                priorityTopics: priorityTopics.data
            };

            return detailedData;
        } catch (error) {
            console.error('Error fetching survey details:', error);
            return processedData.find(r => r.RespondentID === respondentId);
        }
    };

    // Create a TableView component for the existing table content
    const TableView = () => (
        <>
            <div className="content-toolbar">
                <div className="toolbar-left">
                    <div className="search-box">
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                        <input
                            type="text"
                            placeholder="Search responses..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                    <button 
                        className={`btn-filter ${showFilters ? 'active' : ''}`}
                        onClick={() => setShowFilters(!showFilters)}>
                        <FontAwesomeIcon icon={faFilter} /> Filters
                    </button>
                </div>
                <div className="toolbar-right">
                    <button className="btn-add" onClick={handleNewSurvey}>
                        <FontAwesomeIcon icon={faPlus} /> Export Data
                    </button>
                </div>
            </div>

            {showFilters && <FilterPanel data={surveyData.summary?.responses || []} />}

            <div className="surveys-table">
                <table>
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('Created')}>
                                Created Date {renderSortIcon('Created')}
                            </th>
                            <th onClick={() => handleSort('RespondentID')}>
                                Response ID {renderSortIcon('RespondentID')}
                            </th>
                            <th onClick={() => handleSort('CompanyType')}>
                                Company Type {renderSortIcon('CompanyType')}
                            </th>
                            <th onClick={() => handleSort('CompanyStructure')}>
                                Company Structure {renderSortIcon('CompanyStructure')}
                            </th>
                            <th onClick={() => handleSort('NumberofFTEsinIreland')}>
                                FTE Count {renderSortIcon('NumberofFTEsinIreland')}
                            </th>
                            <th onClick={() => handleSort('priorityTopicsCount')}>
                                Priority Topics {renderSortIcon('priorityTopicsCount')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {getPaginatedData(processedData).map(response => (
                            <tr 
                                key={response.RespondentID}
                                onClick={() => handleRowClick(response)}
                                className="clickable-row"
                            >
                                <td>
                                    {new Date(response.Created || response.createdDateTime).toLocaleDateString('en-IE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </td>
                                <td>{response.RespondentID}</td>
                                <td>{response.CompanyType || 'N/A'}</td>
                                <td>{response.CompanyStructure || 'N/A'}</td>
                                <td>{response.NumberofFTEsinIreland || 'N/A'}</td>
                                <td>{response.priorityTopicsCount || 0}</td>
                            </tr>
                        ))}
                        {(!processedData || processedData.length === 0) && (
                            <tr>
                                <td colSpan="6" className="empty-state">
                                    No survey responses found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <Pagination />
            </div>
        </>
    );

    // Create a ReportsView component (placeholder for now)
    const ReportsView = () => (
        <div className="reports-view">
            <h3>Analytics & Reports</h3>
            <DashboardCharts surveyData={surveyData} />
        </div>
    );

    if (loading) {
        return (
            <div className="loading-container">
                <FontAwesomeIcon icon={faSpinner} spin />
                <p>Loading survey data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={fetchSurveyData}>Retry</button>
            </div>
        );
    }

    // Calculate priority counts
    const priorityCounts = surveyData.priorities ? {
        high: surveyData.priorities.high?.length || 0,
        medium: surveyData.priorities.medium?.length || 0,
        low: surveyData.priorities.low?.length || 0,
        none: surveyData.priorities.none?.length || 0
    } : { high: 0, medium: 0, low: 0, none: 0 };


    // Company Type Distribution
    const companyTypes = {};
    surveyData.summary?.responses?.forEach(response => {
        companyTypes[response.CompanyType] = (companyTypes[response.CompanyType] || 0) + 1;
    });



    // Innovation Maturity Distribution
    const maturityLevels = {
        'Ad-hoc': 0,
        'Defined': 0,
        'Managed': 0,
        'Optimized': 0,
        'Advanced': 0
    };

    surveyData.summary?.responses?.forEach(response => {
        if (response.InnovationMaturity) {
            maturityLevels[response.InnovationMaturity] = 
                (maturityLevels[response.InnovationMaturity] || 0) + 1;
        }
    });

    const maturityData = {
        labels: Object.keys(maturityLevels),
        datasets: [{
            label: 'Number of Companies',
            data: Object.values(maturityLevels),
            backgroundColor: [
                'rgba(24, 144, 255, 0.8)',   // Blue
                'rgba(47, 84, 235, 0.8)',    // Royal Blue
                'rgba(114, 46, 209, 0.8)',   // Purple
                'rgba(235, 47, 150, 0.8)',   // Pink
                'rgba(250, 140, 22, 0.8)'    // Orange
            ],
            borderColor: [
                'rgba(24, 144, 255, 1)',
                'rgba(47, 84, 235, 1)',
                'rgba(114, 46, 209, 1)',
                'rgba(235, 47, 150, 1)',
                'rgba(250, 140, 22, 1)'
            ],
            borderWidth: 1,
            borderRadius: 5
        }]
    };

    const maturityOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: true,
                text: 'Innovation Maturity Distribution',
                font: {
                    size: 16,
                    weight: 'bold'
                },
                padding: {
                    bottom: 30
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                },
                title: {
                    display: true,
                    text: 'Number of Companies'
                }
            },
            x: {
                ticks: {
                    autoSkip: false,
                    maxRotation: 45, // Angle the labels slightly
                    minRotation: 45  // Keep it consistent
                }
            }
        }
    };

    return (
        <div className='surveys-container'>
            <div className="content-header">
                <h2>Innovation Survey</h2>
                <p className="text-muted">View and analyse survey responses</p>
            </div>

            <div className="survey-stats">
                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="stat-details">
                        <h3>Total Responses</h3>
                        <p className="stat-number">{surveyData.responseCount}</p>
                       
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faChartBar} />
                    </div>
                    <div className="stat-details">
                        <h3>High Priority Items</h3>
                        <p className="stat-number">{priorityCounts.high}</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faFileAlt} />
                    </div>
                    <div className="stat-details">
                        <h3>Total Topics</h3>
                        <p className="stat-number">
                            {Object.values(priorityCounts).reduce((a, b) => a + b, 0)}
                        </p>
                    </div>
                </div>
            </div>

            <div className="tabs-container">
                <div className="tabs-header">
                    <button 
                        className={`tab-button ${activeTab === 'table' ? 'active' : ''}`}
                        onClick={() => setActiveTab('table')}
                    >
                        <FontAwesomeIcon icon={faTable} /> Survey Responses
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`}
                        onClick={() => setActiveTab('reports')}
                    >
                        <FontAwesomeIcon icon={faChartPie} /> Analytics & Reports
                    </button>
                </div>

                <div className="tab-content">
                    {activeTab === 'table' ? <TableView /> : <ReportsView />}
                </div>
            </div>

            <SurveyDetailsModal
                isOpen={!!selectedResponse}
                onClose={() => setSelectedResponse(null)}
                data={selectedResponse}
                loading={detailsLoading}
            />

            <Modal isOpen={showComingSoon} onClose={() => setShowComingSoon(false)}>
                <div className="coming-soon-modal">
                    <div className="coming-soon-icon">
                        <FontAwesomeIcon icon={faClock} />
                    </div>
                    <h2>Export Feature Coming Soon!</h2>
                    <p>The data export feature is currently under development. Check back soon!</p>
                    <button className="btn-primary" onClick={() => setShowComingSoon(false)}>
                        Got it
                    </button>
                </div>
            </Modal>

            
        </div>
    );
};

const DashboardCharts = ({ surveyData }) => {
    // Add this debug log at the start
   

    // Priority Distribution Data
    const priorityData = {
        labels: ['High', 'Medium', 'Low'],
        datasets: [{
            data: [
                surveyData.priorities?.high?.length || 0,
                surveyData.priorities?.medium?.length || 0,
                surveyData.priorities?.low?.length || 0
            ],
            backgroundColor: ['#d4380d', '#faad14', '#52c41a'],
            borderWidth: 0
        }]
    };

    // Company Type Distribution Data
    const companyTypes = {};
    surveyData.summary?.responses?.forEach(response => {
        if (response.CompanyType) {
            companyTypes[response.CompanyType] = (companyTypes[response.CompanyType] || 0) + 1;
        }
    });

    const companyTypeData = {
        labels: Object.keys(companyTypes),
        datasets: [{
            data: Object.values(companyTypes),
            backgroundColor: ['#1890ff', '#13c2c2', '#722ed1', '#eb2f96', '#fa8c16'],
            borderWidth: 0
        }]
    };

    // Maturity Data
    const maturityLevels = {
        'Ad-hoc': 0,
        'Defined': 0,
        'Managed': 0,
        'Optimized': 0,
        'Advanced': 0
    };

    surveyData.summary?.responses?.forEach(response => {
        if (response.InnovationMaturity) {
            maturityLevels[response.InnovationMaturity] = 
                (maturityLevels[response.InnovationMaturity] || 0) + 1;
        }
    });

    const maturityData = {
        labels: Object.keys(maturityLevels),
        datasets: [{
            label: 'Number of Companies',
            data: Object.values(maturityLevels),
            backgroundColor: [
                'rgba(24, 144, 255, 0.8)',
                'rgba(47, 84, 235, 0.8)',
                'rgba(114, 46, 209, 0.8)',
                'rgba(235, 47, 150, 0.8)',
                'rgba(250, 140, 22, 0.8)'
            ],
            borderWidth: 1,
            borderRadius: 5
        }]
    };

    const maturityOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: true,
                text: 'Innovation Maturity Distribution',
                font: {
                    size: 16,
                    weight: 'bold'
                },
                padding: {
                    bottom: 30
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                },
                title: {
                    display: true,
                    text: 'Number of Companies'
                }
            },
            x: {
                ticks: {
                    maxRotation: 45,
                    minRotation: 45
                }
            }
        }
    };

    // Process data for Innovation Barriers
    const processBarriers = () => {
        if (!surveyData?.summary?.responses) {

            return { labels: [], data: [] };
        }

        const barrierCounts = {};
        surveyData.summary.responses.forEach(response => {
            try {
                if (response?.questionnaireData?.barriers) {
                    response.questionnaireData.barriers.forEach(barrier => {
                        if (barrier) {
                            barrierCounts[barrier] = (barrierCounts[barrier] || 0) + 1;
                        }
                    });
                }
            } catch (error) {
                console.error('Error processing barriers for response:', error);
            }
        });

        const sortedBarriers = Object.entries(barrierCounts)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 5);

        return {
            labels: sortedBarriers.map(([barrier]) => barrier),
            data: sortedBarriers.map(([,count]) => count)
        };
    };

    // Process data for Collaboration Types
    const processCollaborations = () => {
        if (!surveyData?.summary?.responses) {
            return { labels: [], data: [] };
        }

        const collabTypes = {
            'Research Partnerships': 0,
            'University Collaboration': 0,
            'Joint Ventures': 0,
            'Startup Investments': 0,
            'Other Innovation': 0
        };

        surveyData.summary.responses.forEach(response => {
            try {
                // Access the questionnaireData property
                const { questionnaireData } = response;
                
                if (questionnaireData?.collaborationTypes) {
                    
                    
                    // Process each collaboration type
                    questionnaireData.collaborationTypes.forEach(collab => {
                        // Map the collaboration types to our chart categories
                        if (collab.includes('Research')) collabTypes['Research Partnerships']++;
                        if (collab.includes('university') || collab.includes('University')) collabTypes['University Collaboration']++;
                        if (collab.includes('Joint') || collab.includes('joint')) collabTypes['Joint Ventures']++;
                        if (collab.includes('Startup') || collab.includes('startup')) collabTypes['Startup Investments']++;
                        if (collab.includes('Other')) collabTypes['Other Innovation']++;
                    });
                }

                // Also check bestOutcomes as they indicate successful collaborations
                if (questionnaireData?.bestOutcomes) {
                    
                    
                    questionnaireData.bestOutcomes.forEach(outcome => {
                        if (outcome.includes('Research')) collabTypes['Research Partnerships']++;
                        if (outcome.includes('university') || outcome.includes('3rd level')) collabTypes['University Collaboration']++;
                        if (outcome.includes('Joint') || outcome.includes('joint')) collabTypes['Joint Ventures']++;
                        if (outcome.includes('Startup') || outcome.includes('startup')) collabTypes['Startup Investments']++;
                        if (outcome.includes('Other')) collabTypes['Other Innovation']++;
                    });
                }
            } catch (error) {
                console.error('Error processing collaborations for response:', error);
            }
        });

        

        return {
            labels: Object.keys(collabTypes),
            data: Object.values(collabTypes)
        };
    };

    // Process data for Product Strategy
    const processProductStrategy = () => {
        if (!surveyData?.summary?.responses) {
            return { labels: [], data: [] };
        }

        const strategies = {
            'Product Development': [],
            'Market Penetration': [],
            'Diversification': [],
            'Market Development': []
        };

        surveyData.summary.responses.forEach(response => {
            try {
                if (response?.questionnaireData?.productStrategy?.categories) {
                    response.questionnaireData.productStrategy.categories.forEach(category => {
                        if (strategies.hasOwnProperty(category.name)) {
                            strategies[category.name].push(category.percentage);
                        }
                    });
                }
            } catch (error) {
                console.error('Error processing product strategy for response:', error);
            }
        });

        const averages = Object.values(strategies).map(vals => 
            vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0
        );

        return {
            labels: Object.keys(strategies),
            data: averages
        };
    };


    // Create chart data objects with safety checks
    const barriersData = processBarriers();
    const collaborationsData = processCollaborations();
    const productStrategyData = processProductStrategy();

    // Create the chart data objects using the processed data
    const barriersChart = {
        data: {
            labels: barriersData.labels,
            datasets: [{
                label: 'Times Mentioned',
                data: barriersData.data,
                backgroundColor: 'rgba(24, 144, 255, 0.8)',
                borderRadius: 5
            }]
        },
        options: {
            indexAxis: 'y',
            plugins: {
                legend: { display: false },
                title: {
                    display: true,
                    text: 'Top Innovation Barriers',
                    font: { size: 16, weight: 'bold' }
                }
            },
            scales: {
                x: {
                    beginAtZero: true,
                    title: { display: true, text: 'Number of Companies' }
                }
            }
        }
    };

    const collaborationChart = {
        data: {
            labels: collaborationsData.labels,
            datasets: [{
                label: 'Number of Companies',
                data: collaborationsData.data,
                backgroundColor: 'rgba(114, 46, 209, 0.8)',
                borderRadius: 5,
                maxBarThickness: 50
            }]
        },
        options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                title: {
                    display: true,
                    text: 'Collaboration Types',
                    font: { size: 16, weight: 'bold' }
                }
            },
            scales: {
                x: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: 'Number of Companies'
                    },
                    ticks: {
                        stepSize: 1
                    }
                },
                y: {
                    ticks: {
                        autoSkip: false,
                        font: {
                            size: 12
                        }
                    }
                }
            }
        }
    };

    const productStrategyChart = {
        data: {
            labels: productStrategyData.labels,
            datasets: [{
                label: 'Average Percentage',
                data: productStrategyData.data,
                backgroundColor: 'rgba(250, 140, 22, 0.2)',
                borderColor: 'rgba(250, 140, 22, 1)',
                borderWidth: 2,
                pointBackgroundColor: 'rgba(250, 140, 22, 1)',
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgba(250, 140, 22, 1)'
            }]
        },
        options: {
            plugins: {
                legend: { display: false },
                title: {
                    display: true,
                    text: 'Product Strategy Distribution',
                    font: { size: 16, weight: 'bold' }
                }
            },
            scales: {
                r: {
                    beginAtZero: true,
                    max: 100,
                    ticks: { stepSize: 20 }
                }
            }
        }
    };

    return (
        <div className="dashboard-charts">
            {!surveyData?.summary?.responses ? (
                <div className="loading-message">Loading chart data...</div>
            ) : (
                <div className="chart-row">
                    <div className="chart-container">
                        <Doughnut 
                            data={priorityData}
                            options={{
                                plugins: {
                                    legend: { position: 'bottom' }
                                },
                                maintainAspectRatio: false
                            }}
                        />
                    </div>
                    <div className="chart-container">
                        <Pie 
                            data={companyTypeData}
                            options={{
                                plugins: {
                                    legend: { position: 'bottom' }
                                },
                                maintainAspectRatio: false
                            }}
                        />
                    </div>
                    <div className="chart-container">
                        <Bar 
                            data={maturityData}
                            options={maturityOptions}
                        />
                    </div>
                </div>
            )}

            {/* Second Row */}
            <div className="chart-row">
                <div className="chart-container">
                    <Bar 
                        data={barriersChart.data}
                        options={barriersChart.options}
                    />
                </div>
                <div className="chart-container">
                    <Bar 
                        data={collaborationChart.data}
                        options={collaborationChart.options}
                    />
                </div>
            </div>

            {/* Third Row */}
            <div className="chart-row">
                <div className="chart-container">
                    <Radar 
                        data={productStrategyChart.data}
                        options={productStrategyChart.options}
                    />
                </div>
            </div>
        </div>
    );
};

export default SurveysManagement; 